import { Box, Text } from "@chakra-ui/react";
import React from "react";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import {
  entityProfileModalBorders,
  entityProfileModalColors,
} from "../../../../entityProfileModal/entityProfileModal.theme";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";
import { getCaregiverFullName } from "../../../caregiverProfileModal.utils";

type Props = {
  caregiver?: CaregiverProfileCaregiver;
  caregiverId: CaregiverId;
};

type AngularWindow = Window &
  typeof globalThis & {
    angular?: {
      element: (element: Element | Document) => {
        injector?: () => {
          get: <T = unknown>(name: string) => T;
        };
      };
    };
  };

type AngularScope = {
  $applyAsync: () => void;
  $destroy: () => void;
  caregiverFullName: string;
  caregiverId: CaregiverId;
};

type AngularRootScope = {
  $new: (isolated: boolean) => AngularScope;
};

type AngularCompile = (element: Element) => (scope: AngularScope) => void;

const complianceHostStyleId = "react-caregiver-compliance-host-styles";
let complianceHostStyleReferences = 0;

function mountComplianceHostStyles() {
  complianceHostStyleReferences += 1;

  if (document.getElementById(complianceHostStyleId) === null) {
    const style = document.createElement("style");
    style.id = complianceHostStyleId;
    style.textContent = `
.react-caregiver-compliance-host {
  min-width: 0;
  width: 100%;
}

.react-caregiver-compliance-host caregiver-compliance {
  display: block;
  min-width: 0;
  width: 100%;
}

.react-caregiver-compliance-host .box.table-responsive.scroll {
  border: 0;
  margin-bottom: 0;
  max-width: 100%;
  /* Both axes must stay visible. The search/status filters and the per-row
     PDF-actions popover all render inline inside this box, and setting
     overflow-x to anything but visible forces overflow-y to compute to auto
     (per spec), which clips those menus whenever the box is short -- e.g. after
     filtering to a status that matches no items. Nothing here needs to scroll:
     Bootstrap only scrolls .table-responsive below 768px, and the table is
     width: 100% so it reflows instead of overflowing. */
  overflow: visible;
}

.react-caregiver-compliance-host .caregiver-compliance-items-filter .dropdown-menu {
  max-height: none;
}

.react-caregiver-compliance-host .dropdown-menu,
.react-caregiver-compliance-host .popover {
  z-index: 1065;
}

body > .modal.compliance-item-edit-modal,
body > .modal.compliance-instance-modal {
  z-index: 1065 !important;
}

body > .modal.compliance-item-edit-modal .modal-dialog,
body > .modal.compliance-instance-modal .modal-dialog {
  margin: 30px auto;
  max-width: calc(100vw - 64px);
  width: min(1100px, calc(100vw - 64px));
}

body > .modal.compliance-item-edit-modal .modal-content,
body > .modal.compliance-instance-modal .modal-content {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 60px);
}

body > .modal.compliance-item-edit-modal .modal-body,
body > .modal.compliance-instance-modal .modal-body {
  justify-content: flex-start;
  max-height: calc(100vh - 150px);
  overflow: auto;
}
`;
    document.head.appendChild(style);
  }

  return () => {
    complianceHostStyleReferences = Math.max(0, complianceHostStyleReferences - 1);

    if (complianceHostStyleReferences === 0) {
      document.getElementById(complianceHostStyleId)?.remove();
    }
  };
}

export default function CaregiverProfileComplianceSection(props: Props) {
  const hostRef = React.useRef<HTMLDivElement | null>(null);
  const [isAngularUnavailable, setIsAngularUnavailable] = React.useState(false);
  const caregiverFullName =
    props.caregiver === undefined
      ? `Caregiver #${props.caregiverId}`
      : getCaregiverFullName(props.caregiver);
  const caregiverId = props.caregiverId;

  React.useEffect(() => mountComplianceHostStyles(), []);

  React.useEffect(() => {
    const host = hostRef.current;
    const angular = (window as AngularWindow).angular;
    const injector = angular?.element(document.body).injector?.();

    if (host === null || injector === undefined) {
      setIsAngularUnavailable(true);
      return undefined;
    }

    setIsAngularUnavailable(false);

    const rootScope = injector.get<AngularRootScope>("$rootScope");
    const compile = injector.get<AngularCompile>("$compile");
    const scope = rootScope.$new(true);
    const element = document.createElement("caregiver-compliance");

    // Full parity is delegated to Angular because the component owns the table,
    // uploads, add/edit modals, file preview, and PDF workflows.
    scope.caregiverId = caregiverId;
    scope.caregiverFullName = caregiverFullName;
    element.setAttribute("caregiver-id", "caregiverId");
    element.setAttribute("caregiver-full-name", "caregiverFullName");

    host.replaceChildren(element);
    compile(element)(scope);
    scope.$applyAsync();

    return () => {
      scope.$destroy();
      host.replaceChildren();
    };
  }, [caregiverFullName, caregiverId]);

  if (isAngularUnavailable) {
    return (
      <Box
        bg={entityProfileModalColors.surfaceMuted}
        border={entityProfileModalBorders.panel}
        borderRadius="2px"
        color={entityProfileModalColors.secondaryText}
        px={4}
        py={5}
      >
        <Text>Compliance is available only inside the Angular shell.</Text>
      </Box>
    );
  }

  return <Box ref={hostRef} className="react-caregiver-compliance-host" minH="160px" />;
}
